import {HttpClient, HttpEvent, HttpEventType} from "@angular/common/http";
import {InjectionToken} from "@angular/core";
import {BaseService} from "@app/shared/services/api/base-service";
import {GQLQuery, HttpUtilityService, PagingParams} from "@app/shared/services/api/http-utility-service";
import {Observable, last, map} from "rxjs";
import {AttachmentLinkSharedBase, AttachmentSharedBase, AttachmentUploadUrl} from "./attachments.interface";
import {ClassConstructor, ClassTransformer} from "class-transformer";
import Fields from "gql-query-builder/build/Fields";
import * as gql from 'gql-query-builder';
import VariableOptions from "gql-query-builder/build/VariableOptions";
import {PagedModel} from "@app/shared/api/model/paged-model";
import {Moment} from 'moment-timezone';
import {APIResponse} from "@app/layout/admin/services/api/api.interface";


export abstract class AttachmentService<T extends AttachmentSharedBase<AttachmentLinkSharedBase>, RecordId, UploadUrlData extends AttachmentUploadUrl, AssociateRequest> extends BaseService {
  protected abstract readonly recordPath: string;
  protected abstract readonly attachmentIdKey: string;
  protected abstract readonly recordIdKey: string;
  protected abstract readonly linkExpirationKey: string;
  /** Key for the actual upload url in the response from getting upload urls */
  protected abstract readonly uploadUrlKey: string;
  protected abstract readonly implementationClass: ClassConstructor<T>;
  protected abstract readonly attachmentDataKey: string;
  protected abstract readonly graphqlVariableOption: VariableOptions;
  protected readonly hasPreview = false;
  protected readonly PAGE_SIZE = 100;

  protected abstract buildAssociateAttachmentRequestBody(attachmentUrls: UploadUrlData[], recordId: RecordId): AssociateRequest;
  protected abstract extractAssociateResponseAttachments(response: Object): T[];
  protected abstract graphql<Type>(query: GQLQuery, responseProperty?: string, variables?: any): Observable<PagedModel<Type>>;

  constructor(private http: HttpClient,
              protected httpUtilityService: HttpUtilityService,
              protected classTransformer: ClassTransformer) {
    super();
  }

  getAllAttachments(recordId: RecordId): Observable<T[]> {
    return this.httpUtilityService.pageUntilComplete(
      pageInfo => this.getAttachments(recordId, {limit: pageInfo.rows, start_index: pageInfo.first}),
      {first: 0, rows: this.PAGE_SIZE}
    );
  }

  getAttachmentId(attachment: T): string {
    return attachment[this.attachmentIdKey];
  }

  getExpirationTs(link: AttachmentLinkSharedBase): Moment {
    return link[this.linkExpirationKey];
  }

  getUploadUrl(uploadUrlData: UploadUrlData): string {
    return uploadUrlData[this.uploadUrlKey];
  }

  getAttachmentUploadUrls(fileNames: string[]): Observable<UploadUrlData[]> {
    const options = {headers: this.getHeaders()};
    return this.http.post<APIResponse<{upload_urls: UploadUrlData[]}>>(this.getEndpoint() + '/attachments/upload-urls', {file_names: fileNames}, options)
      .pipe(map(response => this.httpUtilityService.extractResponseData<UploadUrlData[]>(response, 'upload_urls')));
  }

  uploadFile(url: string, file: File, progressCallback: (filename: string, progress: number) => void, completeCallback: (filename: string) => void): Observable<void> {
    // need to inline the options: https://github.com/angular/angular/issues/18424
    return this.http.put(url, file, {
      reportProgress: true,
      observe: 'events'
    }).pipe(
        map((event: HttpEvent<any>) => {
          if (event) {
            if (event.type === HttpEventType.UploadProgress) {
              const percentDone = Math.round(100 * event.loaded / event.total);
              progressCallback(file.name, percentDone);
            } else if (event.type === HttpEventType.Response) {
              completeCallback(file.name);
            }
          }
        }),
        last()
    );
  }

  associateAttachments(recordId: RecordId, attachmentUrls: UploadUrlData[]): Observable<T[]> {
    const options = {headers: this.getHeaders()};
    const payload = this.buildAssociateAttachmentRequestBody(attachmentUrls, recordId);
    return this.http.post(this.getAttachmentEndpoint(recordId) , payload, options).pipe(
      map(response => this.extractAssociateResponseAttachments(response))
    );
  }

  deleteAttachment(recordId: RecordId, attachmentId: string): Observable<any> {
    const options = {headers: this.getHeaders()};
    return this.http.delete(this.getAttachmentEndpoint(recordId, attachmentId), options);
  }

  private getAttachments(recordId: RecordId, pageInfo: PagingParams): Observable<PagedModel<T[]>> {
    const linkFields = {link: ['url', this.linkExpirationKey]};
    const fields: Fields = [
      this.recordIdKey,
      this.attachmentIdKey,
      'author_id',
      'mime_type',
      linkFields,
      'file_name'
    ];
    if (this.hasPreview) {
      fields.push({preview: ['height', 'width', linkFields]});
    }
    const variables = this.graphqlVariableOption;
    const fullSearchTerm = {[this.recordIdKey]: recordId, ...pageInfo};
    const query: GQLQuery = gql.query({operation: this.attachmentDataKey, variables, fields});
    return this.graphql(query, this.attachmentDataKey, fullSearchTerm).pipe(
      map(result => ({
        data: this.classTransformer.plainToInstance(this.implementationClass, result[this.DATA_PREFIX] as unknown[]),
        count: result.count
      }))
    );
  }

  private getEndpoint(): string {
    return this.getGateway() + '/' + this.recordPath;
  }

  private getAttachmentEndpoint(recordId: RecordId, attachmentId?: string): string {
    let endpoint = this.getEndpoint() + '/' + recordId + '/attachments';
    if (attachmentId != null) {
      endpoint += '/' + attachmentId;
    }
    return endpoint;
  }
}

export const AttachmentServiceToken = new InjectionToken<AttachmentService<any, any, any, any>>('attachment-service');